import { closestCorners, DndContext, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { round } from 'lodash';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { NotificationBanner } from 'src/components/widgets/notificationBanner/notificationBanner';
import { Section } from 'src/components/widgets/section';
import { BasicBetaButton } from 'src/shared/globals';
import { backofficeUrl, getUrlWithQueryParams, openFileDialog, stringFormat, T, toGlobalID, uploadFile, uuid, validateInputSecurity } from 'src/shared/helpers';
import { useJsonProductsImportEnabled } from 'src/shared/hooks/useJsonProductsImportEnabled';
import MdDragIndicator from 'src/shared/material_icons/action/drag_indicator.svg';

import { useSideImagesNoCreditsDialog } from 'src/components/pages/productConfiguration/dialogs/sideImagesNoCreditsDialog';
import Sparkles from 'src/components/widgets/svg/sparkles';
import { AIGradientLink } from 'src/components/widgets/ai';
import JSONIcon from 'src/shared/material_icons/editor/data_object.svg';
import { dialogsPortal } from 'src/shared/portals';
import { validationContext } from 'src/shared/validation';
import styled from 'styled-components';
import { BasicButton, GhostButton, PrimaryButton } from '../../../../widgets/buttons';
import { Dialog, DialogWindow, InputDialog, MessageDialog, QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import { PageFooterButtons } from '../../../../widgets/headers';
import { Icon } from '../../../../widgets/icon';
import { ContainerBase } from '../../../../widgets/layouts';
import { showErrorNotification, showNotification, showWarningNotification } from '../../../../widgets/notificationToaster';
import { PageHeaderContainer } from '../../../../widgets/pageHeader';
import { SwitchGroup } from '../../../../widgets/switchGroup';
import { ZTooltip } from '../../../../widgets/ztooltip';
import { ValidationBox } from '../../../../widgets/validation/validationBox';
import {
    addSideAction,
    AiResolvedSide,
    applyAiSidesAction,
    editProductNameAction,
    duplicateSideAction,
    editSideMandatoryAllVariants,
    editSideNameAction,
    editSideRotateAllVariants,
    findModelColorForModelSide,
    getAllModelSides,
    getProductIsResizable,
    mergeModelSideAction,
    removeAllColorsAction,
    removeModelOptionsAction,
    removeSideAction,
    reorderSidesAction,
    setAreasWereDeletedAction,
    setHeaderDescriptionCustomComponent,
    setProductImageAction,
    setProductProviderCodeAction,
    setProductResizable,
    uploadProductImageProgressAction,
    upsertModelOptionsAction,
    useProductConfigurationChangeStep,
    useProductConfigurationState
} from '../../state';
import { isDefaultColor } from '../../state/model/modelColor';
import { ModelOptions } from '../../state/model/modelOptions';
import { defaultModelSideImageUrl, ModelSide } from '../../state/model/modelSide';
import { useModelSideManager } from '../../state/model/modelSideManager';
import { subscriptionQuery } from '../../state/queries/__generated__/subscriptionQuery.graphql';
import { subscription } from '../../state/queries/subscription';
import TemplateCatalog from '../../templateCatalog/templateCatalog';
import { printAreasQuery } from './__generated__/printAreasQuery.graphql';
import { PrintAreaEditorDialog } from './printAreaEditorDialog';
import PrintAreaItem from './printAreaItem';
import ResizablePrintArea from './resizablePrintArea/resizablePrintArea';
import { generateSideImage } from './sideGenerator';
import { ExistingSideRef, SideAiGenerateDialog, SideImageAiOptions } from '../4-variants/variants';
import { AiProductCreationDialog, AiProductPlan } from './aiProductCreationDialog';
import { createAreaFromBoundsPX, createDefaultCenteredArea, ModelArea } from '../../state/model/modelArea';
import { AIStudioNoCreditsError } from 'src/components/pages/aiStudio/tasks';
import { useGenerationPreflight } from 'src/components/pages/aiStudio/hooks/useGenerationPreflight';

const PageContainer = styled(ContainerBase)`
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 768px;
    margin: auto;
    /* Minimal clearance for the thin fixed footer; kept small so short content doesn't force a scrollbar. */
    padding-bottom: 24px;
`;

export const LargeMessageDialogWindow = styled(DialogWindow)`
    max-width: 700px;
`;

export const TemplatesDialogWindow = styled(DialogWindow)`
    max-width: 1600px;
    height: 100%;
`;

const ResizableSwitchContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ResizableInfoIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 1.5px solid #d1d5db;
    font-size: 9px;
    font-weight: 700;
    font-style: italic;
    color: #9ca3af;
    line-height: 1;
    flex-shrink: 0;
    cursor: help;
    transition: all 0.15s;

    &:hover {
        border-color: #9ca3af;
        color: #6b7280;
        background: #f3f4f6;
    }
`;

const JSONIconContainer = styled(Icon)`
    width: 28px;
    height: 28px;

    svg {
        color: ${(props) => props.theme.app.primary_dark};
    }
`;

// Setup layout. The side-thumbnails column is fixed to the left edge (overlaying the margin) so that
// showing/hiding it never reflows or shifts the central side-management block.
const SetupLayout = styled.div`
    position: relative;
    width: 100%;
`;

const SideThumbnailColumn = styled.div`
    position: fixed;
    left: 16px;
    top: 84px;
    width: 92px;
    max-height: calc(100vh - 180px);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px;
    z-index: 1;
`;

const SideThumb = styled.button`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 6px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;

    &:hover {
        border-color: ${(props) => props.theme.app.primary};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
`;

const SideThumbImage = styled.div`
    width: 100%;
    height: 56px;
    border-radius: 4px;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }
`;

const SideThumbName = styled.span`
    font-size: 10px;
    font-weight: 600;
    color: #37474f;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
`;

const MainColumn = styled.div`
    width: 100%;
`;

// Drag handle for reordering sides (replaces the old up/down sort buttons). Same GhostButton icon
// styling as the delete/copy buttons so the three icons line up.
const DragHandleButton = styled(GhostButton)`
    color: #455a64;
    cursor: grab;

    &:active {
        cursor: grabbing;
    }
`;

const AiPromptBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 8px;
    text-align: center;

    ${Sparkles} {
        width: 20px;
        height: 20px;
    }
`;

// AI prompt shown under the blue banner: pulled tighter since the PageContainer gap makes it too airy.
const AiLinkRow = styled(AiPromptBanner)`
    margin-top: -16px;
    margin-bottom: -16px;
`;

const getDuplicateSideName = (sourceName: string, existingNames: string[]) => {
    const baseName = sourceName.replace(/\s+\d+$/, '').trim() || sourceName.trim() || 'Side';
    let index = 2;
    let candidate = `${baseName} ${index}`;

    while (existingNames.includes(candidate)) {
        index += 1;
        candidate = `${baseName} ${index}`;
    }

    return candidate;
};

// Wraps each side in a dnd-kit sortable node and hands a ready-to-use drag handle to its children.
const SortableSide: FC<{ guid: string; children: (dragHandle: React.ReactNode) => React.ReactNode }> = ({ guid, children }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: guid });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1
    };

    const dragHandle = (
        <DragHandleButton
            ref={setActivatorNodeRef}
            type='button'
            $size='small'
            $type='icon'
            aria-label={T._('Drag to reorder', 'Admin')}
            {...attributes}
            {...listeners}
        >
            <Icon>
                <MdDragIndicator />
            </Icon>
        </DragHandleButton>
    );

    return (
        <div ref={setNodeRef} style={style} id={`setup-side-${guid}`}>
            {children(dragHandle)}
        </div>
    );
};

const PrintAreas: FC<{}> = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { changeImage, setImageFromUrl } = useModelSideManager();
    const { showDialog, closeDialog } = useDialogManager();
    const { showSideImagesNoCreditsDialog } = useSideImagesNoCreditsDialog();
    const { state, dispatch } = useProductConfigurationState();
    const { runWithPreflight } = useGenerationPreflight();
    const [commitParseProductPrompt] = useMutation(graphql`
        mutation printAreasParseProductPromptMutation($input: ParseProductPromptInput!) {
            parseProductPrompt(input: $input) {
                success
                error
                productName
                sides {
                    name
                    imagePrompt
                }
            }
        }
    `);
    const [commitPredictPrintAreas] = useMutation(graphql`
        mutation printAreasPredictPrintAreasMutation($input: PredictPrintAreasInput!) {
            predictPrintAreas(input: $input) {
                success
                error
                imageInfo {
                    width
                    height
                    ppcmAvg
                }
                detections {
                    confidence
                    bboxPixels {
                        x
                        y
                        width
                        height
                    }
                }
            }
        }
    `);
    const changeStep = useProductConfigurationChangeStep();
    const allModelSides = useMemo(() => getAllModelSides(state.product), [state.product]);
    const [previousSidesGuid, setPreviousSidesGuid] = useState<string[]>(allModelSides.map((x) => x.guid));
    const [fileImageToUpload, setFileImageToUpload] = useState<File | null>(null);
    // Holds the AI-generated (hosted) image URL for a side that is being added; applied by the effect below
    // once the new side exists in the state.
    const [aiImageUrlToApply, setAiImageUrlToApply] = useState<string | null>(null);
    const [showMappingDialog, setShowMappingDialog] = useState(false);

    const [currentEditingModelSide, setCurrentEditingModelSide] = useState<ModelSide | null>(null);
    const [currentEditingModelOptions, setCurrentEditingModelOptions] = useState<ModelOptions | null>(null);

    const [providerCode, setProviderCode] = useState(state.product.providerCode);
    const [isResizable, setIsResizable] = useState(getProductIsResizable(state.product));
    const subscriptionResponse = useLazyLoadQuery<subscriptionQuery>(subscription, {});
    const routeParams = useParams();

    const isJsonProductsImportEnabled = useJsonProductsImportEnabled();

    // Queries
    const query = useLazyLoadQuery<printAreasQuery>(
        graphql`
            query printAreasQuery {
                ecommerceSettings {
                    ecommerceName
                    hasProductsApi
                }
                salesChannels(status: ACTIVE) {
                    id
                    ecommerceSettings {
                        ecommerceName
                        remoteUrl
                    }
                    supportedProviders
                }
                seller {
                    marketplace
                }
                generalSettings {
                    isSideImageScalingEnabled
                }
            }
        `,
        {}
    );

    const isSideImageScalingEnabled = query.generalSettings.isSideImageScalingEnabled;

    // On side added, ask for image for the last side added (file upload o immagine AI simulata).
    useEffect(() => {
        const hasNewSide = allModelSides.some((x) => !previousSidesGuid.find((guid) => guid === x.guid));
        if (hasNewSide && (fileImageToUpload || aiImageUrlToApply)) {
            setPreviousSidesGuid(allModelSides.map((x) => x.guid));
            const side = state.sides[state.sides.length - 1];

            state.product.colors
                .flatMap((x) => x.sides)
                .filter((x) => x.originalSideGuid == side.guid)
                .forEach((modelSide) => {
                    if (aiImageUrlToApply) setImageFromUrl(modelSide, aiImageUrlToApply, false);
                    else if (fileImageToUpload) changeImage(modelSide, fileImageToUpload, false, isSideImageScalingEnabled);
                });

            if (aiImageUrlToApply) setAiImageUrlToApply(null);
        }
    }, [allModelSides]);

    // Auto-open print area editor when sideId is in the URL (from product sidebar)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sideIdParam = params.get('sideId');
        if (!sideIdParam) return;

        const sideId = parseInt(sideIdParam, 10);
        const defaultColor = state.product.colors[0];
        if (!defaultColor || isNaN(sideId)) return;

        const modelSide = defaultColor.sides.find((s) => s.sideID === sideId);
        if (!modelSide) return;

        setCurrentEditingModelSide(modelSide);
        setCurrentEditingModelOptions(state.product.options.find((x) => x.colorGuid === defaultColor.guid) ?? null);
        navigate(getUrlWithQueryParams(backofficeUrl('/product/print-area/print-area-editor')), { state: location.state });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // useEffect(() => {
    // 	if (showMappingDialog) {

    // 			setShowMappingDialog(false);

    // 			showDialog('mapping-variants',<ConnectModelDialog
    // 				forAddingNewSalesChannel={false}
    // 				forEditProduct={true}
    // 				buttonSaveText={T._("Save", "Admin")}
    // 				productID={state.product.id ?? ''}
    // 				additionalCodes={state.product.additionalCodes.map(c=> { return {code: c.code, salesChannelID: c.salesChannelId}} )}
    // 				productColors={state.product.colors}
    // 				salesChannels={[]}
    // 				selectedSalesChannelID={state.salesChannelID}
    // 				productSalesChannelID={state.salesChannelID}
    // 				modelColorMappings={state.product.colorMappings}
    // 				merchantProductOptionsConnected={[]}
    // 				onConfirm={(product, salesChannelID, productColorMappingsToSave)=> {

    // 					showNotification({
    // 						type: 'success',
    // 						message: T._('Template imported successfully!', 'Admin'),
    // 					});

    // 					closeDialog('mapping-variants');
    // 					window.location.href = url(`/products`);
    // 				}}
    // 		/>);
    // 		}
    // 	else
    // 		closeDialog('mapping-variants');

    // },[showMappingDialog]);

    // Form validation
    const _validationContext = useContext(validationContext);

    // The submitted flag lives in the wizard-wide validation provider and persists across steps, so it
    // could be already "submitted" on entry and paint the print-area boxes red before any action. Reset
    // it on enter/leave so the validation only shows after the user actually clicks "next" on this step.
    useEffect(() => {
        _validationContext.resetSubmitted();
        return () => _validationContext.resetSubmitted();
    }, []);

    // Use supportedVariants from the state instead of hook
    const isCurrentSalesChannelSupportVariants = state.supportedVariants;

    const handleSkipVariationsClick = () => {
        if (validatePrintAreas()) {
            // Remove all previous variants set for product
            if (!(state.product.colors.length == 1 && state.product.colors[0].colorID == 1)) dispatch(removeAllColorsAction());
            changeStep('preview');
        }
    };

    const handleGoToVariationsButtonClick = () => {
        if (validatePrintAreas()) {
            // Always go to variations when this specific button is clicked (only shown for channels that support variants)
            changeStep('variations');
        }
    };

    // A side blocks proceeding when it has no print area set yet (missing area or an unset scale/ppcm).
    // A single-side product can still proceed once that side's print area is configured, as before.
    const sideNeedsPrintArea = (modelSide: ModelSide) => modelSide.areas.length === 0 || modelSide.ppcm === 0;

    const validatePrintAreas = () => {
        // Light up the per-side ValidationBoxes (red outline + inline message) and scroll to the first
        // missing print area in the normal layout. The resizable layout renders no boxes, so submit()
        // reports valid there and we fall back to the imperative check below with its own message.
        if (!_validationContext.submit()) return false;

        let isValid = true;
        for (const modelSide of allModelSides) {
            if (sideNeedsPrintArea(modelSide)) {
                const side = state.sides.find((x) => x.guid == modelSide.originalSideGuid);
                showNotification({
                    message: stringFormat(T._('First set the print area on the side {0} before proceeding to variants.', 'Admin'), side!.name),
                    type: 'danger',
                    dismiss: {
                        duration: 4000
                    }
                });
                isValid = false;
                break;
            }
        }

        return isValid;
    };

    const uploadProductImageFromDevice = () => {
        openFileDialog(async (files) => {
            if (files.length > 0) {
                try {
                    dispatch(uploadProductImageProgressAction(0));
                    const imageUrl = await uploadFile(files[0], isSideImageScalingEnabled);

                    dispatch(setProductImageAction(imageUrl));
                } catch {
                    showNotification({
                        message: T._('An error occurred during uploading image', 'Admin'),
                        type: 'danger',
                        dismiss: {
                            duration: 4000
                        }
                    });
                } finally {
                    dispatch(uploadProductImageProgressAction(null));
                }
            }
        }, '.jpg,.png,.jpeg,.webp,.heic,.avif');
    };

    // The product name must be set before uploading/generating the product image (it lives in the top bar).
    const ensureProductNameSet = () => {
        if (!state.product.name || validateInputSecurity(state.product.name).isValid === false) {
            showNotification({
                message: T._('You need to set the product name', 'Admin'),
                type: 'danger',
                dismiss: {
                    duration: 4000
                }
            });
            return false;
        }
        return true;
    };

    // Direct actions behind the centered upload/AI buttons of the from-scratch side card.
    const handleProductImageUploadFromDevice = () => {
        if (!ensureProductNameSet()) return;
        uploadProductImageFromDevice();
    };

    const realImageUrl = (url?: string | null): string | undefined => (url && url !== defaultModelSideImageUrl ? url : undefined);

    // Every distinct product image (across all variants and sides), usable as a reference for image-to-image
    // AI generation. Deduplicated by image; the color name is added to the label only when there are multiple variants.
    const buildExistingImageRefs = (): ExistingSideRef[] => {
        const hasMultipleColors = state.product.colors.length > 1;
        const refs: ExistingSideRef[] = [];
        const seen = new Set<string>();
        state.product.colors.forEach((color) => {
            color.sides.forEach((ms) => {
                const img = realImageUrl(ms.imageUrl);
                if (!img || seen.has(img)) return;
                seen.add(img);
                const sideName = state.sides.find((s) => s.guid === ms.originalSideGuid)?.name ?? T._('Side', 'Admin');
                refs.push({ imageUrl: img, label: hasMultipleColors && color.name ? `${color.name} / ${sideName}` : sideName });
            });
        });
        return refs;
    };

    // Default reference image: the target side's current image when it has one (edit), otherwise the first
    // side's image of the same variant (new product). Undefined falls back to the first available reference.
    const pickDefaultReferenceImage = (targetSide?: ModelSide): string | undefined => {
        const current = realImageUrl(targetSide?.imageUrl);
        if (current) return current;
        const color = targetSide ? state.product.colors.find((c) => c.sides.some((s) => s.guid === targetSide.guid)) : state.product.colors[0];
        const firstSideGuid = state.sides[0]?.guid;
        return realImageUrl(color?.sides.find((s) => s.originalSideGuid === firstSideGuid)?.imageUrl);
    };

    // Opens the same AI generation modal used on the variants page. It reopens on every click (so an
    // already generated/changed image can be regenerated) and applies the confirmed result via `apply`.
    const openSideAiGenerateDialog = (targetSide: ModelSide | undefined, apply: (url: string) => void) => {
        const existingSides = buildExistingImageRefs();
        showDialog(
            'side-ai-generate',
            <SideAiGenerateDialog
                existingSides={existingSides}
                defaultReferenceImageUrl={pickDefaultReferenceImage(targetSide)}
                allowExistingOption={existingSides.length > 0}
                generatePreview={async (opts: SideImageAiOptions) => {
                    const referenceUrl = opts.mode === 'existing' ? (opts.referenceImageUrl ?? undefined) : undefined;
                    return await generateSideImage({ prompt: opts.prompt, sideImageUrl: referenceUrl });
                }}
                onClose={() => closeDialog('side-ai-generate')}
                onNoCredits={() => {
                    closeDialog('side-ai-generate');
                    showSideImagesNoCreditsDialog();
                }}
                onApply={(url) => {
                    closeDialog('side-ai-generate');
                    apply(url);
                }}
            />
        );
    };

    // Runs the AI print-area predictor on a hosted image URL and returns a ready-to-apply area (highest-confidence
    // detection, or a centered default area when nothing is detected). Null only on failure. PPCM comes from the predictor.
    const predictAreaForImage = (imageUrl: string): Promise<{ area: ModelArea; ppcm: number | null } | null> =>
        new Promise((resolve) => {
            commitPredictPrintAreas({
                variables: { input: { imageUrl, includeAnnotatedImage: false } },
                onCompleted: (response: any) => {
                    const result = response?.predictPrintAreas;
                    const detections = result?.detections ?? [];
                    if (!result?.success) {
                        resolve(null);
                        return;
                    }

                    const ppcm = result.imageInfo?.ppcmAvg ?? null;
                    if (detections.length > 0) {
                        const best = detections.reduce((a: any, b: any) => (b.confidence > a.confidence ? b : a));
                        const box = best.bboxPixels;
                        const imgW = result.imageInfo?.width || box.width;
                        const imgH = result.imageInfo?.height || box.height;
                        resolve({ area: createAreaFromBoundsPX(box.x - imgW / 2, box.y - imgH / 2, box.width, box.height), ppcm });
                        return;
                    }

                    const imageWidth = result.imageInfo?.width;
                    const imageHeight = result.imageInfo?.height;
                    if (imageWidth && imageHeight) {
                        resolve({ area: createDefaultCenteredArea(imageWidth, imageHeight), ppcm });
                        return;
                    }
                    resolve(null);
                },
                onError: () => resolve(null)
            });
        });

    // Parses a natural-language prompt into a product structure via the AI service. Null on failure/unparseable prompt.
    const parsePromptForProduct = (prompt: string): Promise<AiProductPlan | null> =>
        new Promise((resolve) => {
            commitParseProductPrompt({
                variables: { input: { prompt } },
                onCompleted: (response: any) => {
                    const result = response?.parseProductPrompt;
                    if (!result?.success) {
                        resolve(null);
                        return;
                    }
                    resolve({
                        productName: result.productName,
                        sides: (result.sides ?? []).map((side: any) => ({ name: side.name, imagePrompt: side.imagePrompt }))
                    });
                },
                onError: () => resolve(null)
            });
        });

    // New products only: for each parsed side generate an image + predict its print area, then assemble the whole
    // product in a single reducer pass. Preflight gates AI Studio availability (activate addon / purchase credits)
    // before any task is queued. A side that fails is skipped (partial product).
    const generateProductFromPlan = async (plan: AiProductPlan, onProgress: (done: number, total: number, label: string) => void, onDone: () => void) => {
        await runWithPreflight(
            async () => {
                const total = plan.sides.length;
                const resolved: AiResolvedSide[] = [];
                let outOfCredits = false;
                // The first successfully generated side becomes the reference for the remaining sides, so they are
                // rendered image-to-image from the same product (consistent style/color/shape, only the view changes).
                let referenceUrl: string | undefined;

                for (let i = 0; i < plan.sides.length; i++) {
                    const side = plan.sides[i];
                    onProgress(i, total, stringFormat(T._('Generating side "{0}" ({1}/{2})...', 'Admin'), side.name, String(i + 1), String(total)));
                    try {
                        const url = await generateSideImage({ prompt: side.imagePrompt, sideImageUrl: referenceUrl });
                        if (!referenceUrl) referenceUrl = url;
                        const predicted = await predictAreaForImage(url);
                        resolved.push({ name: side.name, imageUrl: url, area: predicted?.area ?? null, ppcm: predicted?.ppcm ?? null });
                    } catch (ex) {
                        if (ex instanceof AIStudioNoCreditsError) {
                            outOfCredits = true;
                            break;
                        }
                        // Other failures: skip this side and keep going (partial product).
                    }
                }

                onProgress(total, total, T._('Creating the product...', 'Admin'));

                if (resolved.length > 0) {
                    dispatch(editProductNameAction(plan.productName));
                    dispatch(applyAiSidesAction(resolved));
                }

                if (outOfCredits) {
                    showSideImagesNoCreditsDialog();
                } else if (resolved.length === 0) {
                    showErrorNotification({ message: T._('The product could not be generated. Please try again.', 'Admin') });
                } else if (resolved.length < total) {
                    showWarningNotification({ message: T._('The product was created partially: some sides could not be generated.', 'Admin') });
                } else {
                    showNotification({
                        type: 'success',
                        message: <>{T._('Product created with AI. Remember to save to apply the changes.', 'Admin')}</>,
                        dismiss: { duration: 4000 }
                    });
                }
                // The product has been created (or the flow ended without generating): close the creation dialog now.
                onDone();
            },
            // autoRefill: false — side generation must not enable credit auto-refill on first addon activation.
            // onNoCredits also closes the creation dialog after opening the purchase modal.
            {
                autoRefill: false,
                onNoCredits: () => {
                    showSideImagesNoCreditsDialog();
                    onDone();
                }
            }
        );
    };

    // Opens the AI product-creation flow (prompt → confirm → progress). Wired to the "Do you want to use AI to
    // create your product?" entry point; new products only.
    const handleCreateProductWithAi = () => {
        // Safety net: this flow rebuilds the product's sides (rename/add/overwrite), so it must never run on an
        // already-saved product. The entry point is hidden for existing products; this guards against misuse.
        if (state.product.id) return;
        showDialog(
            'ai-create-product',
            <AiProductCreationDialog parsePrompt={parsePromptForProduct} generate={generateProductFromPlan} onClose={() => closeDialog('ai-create-product')} />
        );
    };

    const handleProductImageGenerateWithAi = () => {
        if (!ensureProductNameSet()) return;
        openSideAiGenerateDialog(allModelSides[0], (url) => dispatch(setProductImageAction(url)));
    };

    const handleSideChangeName = (modelSide: ModelSide) => {
        const side = state.sides.find((x) => x.guid == modelSide.originalSideGuid);

        showDialog(
            'side-name',
            <InputDialog
                label={T._('Side name', 'Admin')}
                placeholder={T._('Type the name of the side', 'Composer')}
                defaultValue={side?.name ?? ''}
                onConfirm={(name) => {
                    if (name == '') {
                        showDialog('invalid-name', <MessageDialog message={T._('Please type a valid side name.', 'Admin')} />);
                        return;
                    }
                    closeDialog('side-name');
                    dispatch(editSideNameAction(modelSide, name));
                }}
            />
        );
    };

    const handleSideAddUpload = () => {
        const sideName = `Side ${state.sides.length + 1}`;
        openFileDialog((files) => {
            if (files.length > 0) {
                setFileImageToUpload(files[0]);
                dispatch(addSideAction(sideName));
            }
        }, '.jpg,.png,.jpeg,.webp,.heic,.avif');
    };

    const handleSideAddAi = () => {
        const sideName = `Side ${state.sides.length + 1}`;
        // Open the AI generation modal (same as the other AI buttons); create the new side with the
        // confirmed image only after the user has chosen the prompt/source.
        openSideAiGenerateDialog(undefined, (url) => {
            setAiImageUrlToApply(url);
            dispatch(addSideAction(sideName));
        });
    };

    const showSidePrintAreaWarning = (modelSide: ModelSide) => {
        if (modelSide.areas.length > 0) {
            showDialog(
                'print-area-warning',
                <MessageDialog
                    message={T._(
                        'The side image has been successfully updated. Please ensure that the print area correctly matches the new image, and also review any pre-designed templates, if applicable.',
                        'Admin'
                    )}
                />
            );
        }
    };

    const changeSideImageFromDevice = (modelSide: ModelSide) => {
        openFileDialog((files) => {
            if (files.length > 0) {
                const fileImage = files[0];

                if (!query.generalSettings.isSideImageScalingEnabled) checkImageSize(fileImage);

                if (subscriptionResponse.subscription?.hasSidesPerVariant && state.product.colors.length > 1) {
                    showMultipleVariantsQuestion(
                        modelSide,
                        'IMAGE',
                        () => {
                            changeImage(modelSide, fileImage, false, isSideImageScalingEnabled);
                            showSidePrintAreaWarning(modelSide);
                        },
                        () => {
                            state.product.colors.forEach((modelColor) => {
                                modelColor.sides
                                    .filter((x) => x.originalSideGuid == modelSide.originalSideGuid)
                                    .forEach((modelSide) => {
                                        changeImage(modelSide, fileImage, false, isSideImageScalingEnabled);
                                    });
                            });
                            showSidePrintAreaWarning(modelSide);
                        }
                    );
                } else {
                    changeImage(modelSide, fileImage, false, isSideImageScalingEnabled);
                    showSidePrintAreaWarning(modelSide);
                }
            }
        }, '.jpg,.png,.jpeg,.webp,.heic,.avif');
    };

    // Applies an AI-generated (hosted) image URL to the side across all its colour variants.
    const changeSideImageWithAi = (modelSide: ModelSide, url: string) => {
        if (subscriptionResponse.subscription?.hasSidesPerVariant && state.product.colors.length > 1) {
            state.product.colors.forEach((modelColor) => {
                modelColor.sides.filter((x) => x.originalSideGuid == modelSide.originalSideGuid).forEach((ms) => setImageFromUrl(ms, url, false));
            });
        } else {
            setImageFromUrl(modelSide, url, false);
        }
        showSidePrintAreaWarning(modelSide);
    };

    const handleGenerateImageWithAi = (modelSide: ModelSide) => {
        openSideAiGenerateDialog(modelSide, (url) => changeSideImageWithAi(modelSide, url));
    };

    const checkImageSize = (fileImage: File) => {
        // create an image element with that selected file
        const img = new Image();
        img.src = window.URL.createObjectURL(fileImage);
        // as soon as the image has been loaded
        img.onload = function () {
            const width = img.naturalWidth;
            const height = img.naturalHeight;

            // unload it
            window.URL.revokeObjectURL(img.src);

            // check its dimensions
            if (width > 1024 || height > 1024) {
                showNotification({
                    message: T._('We suggest using images below 1024x1024 pixels to avoid slow loading time of the customizer', 'Admin'),
                    type: 'warning',
                    dismiss: {
                        duration: 10000
                    }
                });
            } else if (fileImage.size / 1024 / 1024 > 2) {
                showNotification({
                    message: T._('We suggest using images below 2MB to avoid slow loading time of the customizer', 'Admin'),
                    type: 'warning',
                    dismiss: {
                        duration: 10000
                    }
                });
            }
        };
    };

    const handleSideRemoveClick = (modelSide: ModelSide) => {
        const side = state.sides.find((x) => x.guid == modelSide.originalSideGuid);

        if (side) {
            showDialog(
                'delete-side',
                <QuestionDialog
                    message={stringFormat(T._(`Do you want remove the side '{0}'?`, 'Admin'), side.name)}
                    onYesClick={() => {
                        closeDialog('delete-side');
                        dispatch(removeSideAction(side));
                    }}
                />
            );
        }
    };

    const handleSideDuplicateClick = (modelSide: ModelSide) => {
        const side = state.sides.find((x) => x.guid == modelSide.originalSideGuid);

        if (!side) return;

        dispatch(
            duplicateSideAction(
                side.guid,
                getDuplicateSideName(
                    side.name,
                    state.sides.map((x) => x.name)
                )
            )
        );
    };

    const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { delay: 50, tolerance: 5 } }), useSensor(TouchSensor));

    const handleSideDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const guids = allModelSides.map((s) => s.originalSideGuid);
        const oldIndex = guids.indexOf(String(active.id));
        const newIndex = guids.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) return;

        dispatch(reorderSidesAction(arrayMove(guids, oldIndex, newIndex)));
    };

    const handleSideChangeMandatory = (modelSide: ModelSide, value: boolean) => {
        //dispatch(mergeModelSideAction(modelSide, { ...modelSide, mandatory: value }));
        dispatch(editSideMandatoryAllVariants(modelSide, value));
    };

    const handleSideChangeRotate = (modelSide: ModelSide, value: boolean) => {
        //dispatch(mergeModelSideAction(modelSide, { ...modelSide, canRotate: value }));
        dispatch(editSideRotateAllVariants(modelSide, value));
    };

    const handleSideChangeArea = async (modelSide: ModelSide) => {
        const modelColor = findModelColorForModelSide(state.product, modelSide)!;

        setCurrentEditingModelSide(modelSide);
        setCurrentEditingModelOptions(state.product.options.find((x) => x.colorGuid === modelColor.guid) ?? null);

        // Manually edit the url
        navigate(getUrlWithQueryParams(backofficeUrl('/product/print-area/print-area-editor')), { state: location.state });
    };

    const handleApplyAreaToAll = (newModelSide: ModelSide, newModelOptions: ModelOptions | null) => {
        if (currentEditingModelSide) {
            state.product.colors.forEach((modelColor) => {
                const thisColorModelSide = modelColor.sides.find((x) => x.originalSideGuid == currentEditingModelSide.originalSideGuid);

                if (thisColorModelSide) {
                    dispatch(
                        mergeModelSideAction(thisColorModelSide, {
                            areas: newModelSide.areas.map((x) => ({
                                ...x,
                                layers: x.layers.map((y) => ({
                                    ...y,
                                    id: toGlobalID(uuid(), 'AreaLayer')
                                }))
                            })),
                            ppcm: newModelSide.ppcm,
                            canRotate: newModelSide.canRotate
                        })
                    );
                }

                if (newModelOptions) {
                    dispatch(upsertModelOptionsAction({ ...newModelOptions, colorGuid: modelColor.guid, optionID: null }));
                } else if (currentEditingModelOptions) {
                    dispatch(removeModelOptionsAction(modelColor));
                }
            });
        }
    };

    const showMultipleVariantsQuestion = (side: ModelSide, type: 'PRINT_AREA' | 'IMAGE', firstCallback: () => void, allCallback: () => void) => {
        const originalSide = state.sides.find((x) => x.guid === side.originalSideGuid)!;

        const message =
            type === 'PRINT_AREA'
                ? stringFormat(
                      T._('This product has multiple variants, do you want to change the {0} side print areas for all variants?', 'Admin'),
                      originalSide.name
                  )
                : stringFormat(
                      T._('This product has multiple variants, do you want to change the {0} side image for all variants?', 'Admin'),
                      originalSide.name
                  );

        showDialog(
            'apply-image',
            <MessageDialog
                windowDecorator={LargeMessageDialogWindow}
                message={message}
                buttons={[
                    {
                        label: T._('Change only this one', 'Admin'),
                        onClick: () => {
                            firstCallback();
                            closeDialog('apply-image');
                        }
                    },
                    {
                        label: T._('Change on all variants', 'Admin'),
                        outline: true,
                        onClick: () => {
                            allCallback();
                            closeDialog('apply-image');
                        }
                    }
                ]}
            />
        );
    };

    const handleOnSelectTemplate = () => {
        showDialog(
            'ChooseTemplate',
            <Dialog title={T._('Select template', 'Admin')} windowDecorator={TemplatesDialogWindow} showCloseButton={true}>
                <TemplateCatalog
                    salesChannelOfflineID={query.salesChannels.find((s) => s.ecommerceSettings?.ecommerceName == 'NONE')?.id ?? ''}
                    targetSalesChannelID={state.salesChannelID}
                    targetEcommerce={state.ecommerceSalesChannel.length > 0 ? state.ecommerceSalesChannel[0] : 'NONE'}
                    targetProductSKU={state.product.code}
                    targetProductName={state.product.name}
                ></TemplateCatalog>
            </Dialog>
        );
    };

    const hangleChangeIsResizable = (value: boolean) => {
        dispatch(setProductResizable(value));

        setIsResizable(value);
    };

    //Detect changes in modelSide to show variants dialog on save
    const hasModelSideChanged = (currentSide: ModelSide, newSide: ModelSide): boolean => {
        if (!currentSide?.areas || !newSide?.areas) return false;
        if (currentSide.areas.length !== newSide.areas.length) return true;

        return currentSide.areas.some((area, i) => {
            const newArea = newSide.areas[i];

            return (
                area.boundsX !== newArea.boundsX ||
                area.boundsY !== newArea.boundsY ||
                area.boundsWidth !== newArea.boundsWidth ||
                area.boundsHeight !== newArea.boundsHeight
            );
        });
    };

    //Detect changes in modelOptions to show variants dialog on save
    const hasModelOptionsChanged = (currentOptions: ModelOptions | null, newOptions: ModelOptions | null): boolean => {
        // If both are null, nothing changed
        if (currentOptions === null && newOptions === null) return false;

        // If only one is null, it's a change
        if (currentOptions === null || newOptions === null) return true;

        const valuesAreDifferent = (a: any, b: any): boolean => {
            if (a === null && b === null) return false;

            if (typeof a === 'number' && typeof b === 'number') {
                return a.toFixed(2) !== b.toFixed(2);
            }

            return a !== b;
        };

        return (Object.keys(newOptions) as (keyof ModelOptions)[]).some((key) => {
            if (currentOptions.hasOwnProperty(key)) {
                return valuesAreDifferent(currentOptions[key], newOptions[key]);
            } else {
                return true; // key exists in newOptions but not in currentOptions
            }
        });
    };

    const handleClickJsonSetting = () => {
        showDialog(
            'JSON-settings-dialog',
            <InputDialog
                label={T._('Entity Reference Key', 'Admin')}
                defaultValue={providerCode ?? ''}
                onConfirm={(providerCode) => {
                    if (providerCode === '') {
                        showErrorNotification({
                            message: T._('Type a valid provider code', 'Admin')
                        });

                        return;
                    }

                    closeDialog('JSON-settings-dialog');

                    setProviderCode(providerCode);
                    dispatch(setProductProviderCodeAction(providerCode));
                }}
            />
        );
    };

    // Set the switch custom component
    useEffect(() => {
        const hasSalesChannel = !state.ecommerceSalesChannel.some((e) => e === 'NONE');

        const resizableSwitch = state.product.id == null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <SwitchGroup label={T._('Resizable product', 'Admin')} checked={isResizable} onChange={hangleChangeIsResizable} />
                <ZTooltip
                    position='bottom'
                    interactive
                    html={
                        <span>
                            {T._(
                                'Enable this to let customers personalize product measurements. Resizable products allow dimension-based customization beyond design.',
                                'Admin'
                            )}{' '}
                            <a
                                href='https://zakeke.zendesk.com/hc/en-us/articles/14234024887964-Resizable-products'
                                target='_blank'
                                rel='noreferrer'
                                style={{ color: '#93c5fd', textDecoration: 'underline' }}
                            >
                                {T._('Learn more', 'Admin')}
                            </a>
                        </span>
                    }
                >
                    <ResizableInfoIcon>i</ResizableInfoIcon>
                </ZTooltip>
            </div>
        );

        // From-scratch products no longer show the name field in the page body (it's managed in the top-bar
        // title), so their JSON bulk-import setting is surfaced in the top bar instead, matching the sales-channel flow.
        const jsonIcon = isJsonProductsImportEnabled && (
            <ZTooltip
                position='bottom'
                title={T._(
                    "Edit JSON setting - This setting is only needed for bulk import functionality to reference the entity in the import JSON. If you don't need the JSON bulk import functionality, you can ignore this parameter.",
                    'Admin'
                )}
            >
                <JSONIconContainer $hoverable onClick={handleClickJsonSetting}>
                    <JSONIcon />
                </JSONIconContainer>
            </ZTooltip>
        );

        if (!hasSalesChannel) {
            dispatch(
                setHeaderDescriptionCustomComponent(
                    <React.Fragment>
                        <ResizableSwitchContainer>
                            {/* JSON setting sits right after the product name, before the resizable switch. */}
                            {jsonIcon}
                            {resizableSwitch}
                        </ResizableSwitchContainer>
                    </React.Fragment>
                )
            );
        } else {
            dispatch(
                setHeaderDescriptionCustomComponent(
                    <React.Fragment>
                        <ResizableSwitchContainer>
                            <PageHeaderContainer.TitleDescription>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {/* Product name now shown in the full-screen wizard title (top-left), so it is not repeated here. */}
                                    {state.product.id != null && isJsonProductsImportEnabled && (
                                        <ZTooltip
                                            position='bottom'
                                            title={T._(
                                                "Edit JSON setting - This setting is only needed for bulk import functionality to reference the entity in the import JSON. If you don't need the JSON bulk import functionality, you can ignore this parameter.",
                                                'Admin'
                                            )}
                                        >
                                            <JSONIconContainer $hoverable onClick={handleClickJsonSetting}>
                                                <JSONIcon />
                                            </JSONIconContainer>
                                        </ZTooltip>
                                    )}
                                </div>
                            </PageHeaderContainer.TitleDescription>
                            {resizableSwitch}
                        </ResizableSwitchContainer>
                    </React.Fragment>
                )
            );
        }

        return () => {
            dispatch(setHeaderDescriptionCustomComponent(null));
        };
    }, [isResizable, state.product.imageUrl, state.product.id, state.ecommerceSalesChannel, isJsonProductsImportEnabled]);

    // The "create from scratch" screen (product name + product image upload) is only for a brand-new
    // offline product that has no image yet. An existing product — even a partial one saved without a
    // product image — must show its (partial) side configuration instead of the from-scratch screen.
    const isNewFromScratch = !state.product.id && !state.product.imageUrl && state.ecommerceSalesChannel.some((e) => e === 'NONE');

    // Left navigation column: shown only when the setup step lists multiple sides.
    const showSideColumn = allModelSides.length > 1 && !isNewFromScratch && (!isResizable || state.product.colors.length > 1);

    const scrollToSide = (guid: string) => {
        document.getElementById(`setup-side-${guid}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Intro blue banner + "use AI" link, shown both before the product image exists (over the skeleton card)
    // and after, so the setup screen reads the same throughout.
    const introBanner = (
        <>
            <NotificationBanner
                type='INFO'
                variant='slim'
                content={
                    <>
                        {T._('Upload your product image, then define the print area.', 'Admin')}{' '}
                        {T._(
                            'If your product has multiple customizable sides — like the front and back of a t-shirt — and multiple variants, you can add them all after uploading the first image.',
                            'Admin'
                        )}
                    </>
                }
            />
            {/* AI product creation always rebuilds the whole product's sides, so it only applies to a product
                that has not been saved yet. Hidden on saved products (where the flow is blocked anyway) instead
                of showing a dead link. It still works on a new product that already has sides/images (the
                reducer reuses the existing sides). */}
            {!state.product.id && (
                <AiLinkRow>
                    <AIGradientLink onClick={handleCreateProductWithAi}>{T._('Do you want to use AI to create your product?', 'Admin')}</AIGradientLink>
                </AiLinkRow>
            )}
        </>
    );

    return (
        <React.Fragment>
            <SetupLayout>
                {showSideColumn && (
                    <SideThumbnailColumn>
                        {allModelSides.map((modelSide) => {
                            const sideName = state.sides.find((s) => s.guid === modelSide.originalSideGuid)?.name ?? T._('Side', 'Admin');
                            return (
                                <SideThumb key={modelSide.guid} type='button' onClick={() => scrollToSide(modelSide.originalSideGuid)} title={sideName}>
                                    <SideThumbImage>
                                        <img src={modelSide.imageUrl || defaultModelSideImageUrl} alt={sideName} />
                                    </SideThumbImage>
                                    <SideThumbName>{sideName}</SideThumbName>
                                </SideThumb>
                            );
                        })}
                    </SideThumbnailColumn>
                )}
                <MainColumn>
                    {isResizable && state.product.colors.length <= 1 && (
                        <ResizablePrintArea
                            onSave={(modelOptions: ModelOptions[]) => {
                                const baseColor = state.product.colors.find((c) => c.colorID === 1)!;
                                dispatch(removeModelOptionsAction(baseColor));
                                modelOptions.forEach((modelOption) => {
                                    dispatch(upsertModelOptionsAction(modelOption));
                                });

                                handleSkipVariationsClick();
                            }}
                        />
                    )}
                    <PageContainer>
                        {isNewFromScratch ? (
                            <>
                                {/* From-scratch products use the same side card as standard products (product image,
                                    "Set print area", side name, options), all shown as a skeleton until the image exists.
                                    The intro banner + AI link are shown here already. The product name is managed in the top bar.
                                    Uploading the side image here also sets the product image (handleProductImageUploadFromDevice). */}
                                {(!isResizable || state.product.colors.length > 1) && (
                                    <>
                                        {introBanner}
                                        {allModelSides[0] && (
                                            <Section>
                                                <PrintAreaItem
                                                    showRemoveButton={false}
                                                    showMandatoryCheckbox={false}
                                                    hasImage={false}
                                                    imageUploading={state.productImageUploadProgress != null}
                                                    imagePlaceholderLabel={T._('Upload or generate your product image', 'Admin')}
                                                    modelColor={findModelColorForModelSide(state.product, allModelSides[0])!}
                                                    modelSide={allModelSides[0]}
                                                    onAreaChangeClick={() => handleSideChangeArea(allModelSides[0])}
                                                    onImageChangeClick={handleProductImageUploadFromDevice}
                                                    onGenerateImageWithAi={handleProductImageGenerateWithAi}
                                                    onNameChangeClick={() => handleSideChangeName(allModelSides[0])}
                                                    onSetRotateSide={(value) => handleSideChangeRotate(allModelSides[0], value)}
                                                    onSetMandatorySide={(value) => handleSideChangeMandatory(allModelSides[0], value)}
                                                />
                                            </Section>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <React.Fragment>
                                {/* Product name is managed only in the top-bar title (with the JSON setting icon next to it),
                                    so it is no longer repeated in the page body. */}
                                {(!isResizable || state.product.colors.length > 1) && (
                                    <>
                                        {introBanner}
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCorners}
                                            modifiers={[restrictToVerticalAxis]}
                                            onDragEnd={handleSideDragEnd}
                                        >
                                            <SortableContext items={allModelSides.map((s) => s.originalSideGuid)} strategy={verticalListSortingStrategy}>
                                                {allModelSides.map((modelSide) => {
                                                    const sideName =
                                                        state.sides.find((s) => s.guid === modelSide.originalSideGuid)?.name ?? T._('this side', 'Admin');
                                                    return (
                                                        <SortableSide key={modelSide.originalSideGuid} guid={modelSide.originalSideGuid}>
                                                            {(dragHandle) => (
                                                                <Section>
                                                                    {/* Missing print area is flagged with the shared ValidationBox on "next" (see validatePrintAreas). */}
                                                                    <ValidationBox
                                                                        fullWidth
                                                                        validationKey={`print-area-${modelSide.originalSideGuid}`}
                                                                        validationValue={modelSide}
                                                                        validate={(ms) => sideNeedsPrintArea(ms)}
                                                                        validationMessage={stringFormat(
                                                                            T._('Please set a print area on the side {0} before proceeding.', 'Admin'),
                                                                            sideName
                                                                        )}
                                                                    >
                                                                        <PrintAreaItem
                                                                            showRemoveButton={state.sides.length > 1}
                                                                            modelColor={findModelColorForModelSide(state.product, modelSide)!}
                                                                            modelSide={modelSide}
                                                                            hasImage={!!modelSide.imageUrl}
                                                                            imagePlaceholderLabel={T._('Upload or generate the side image', 'Admin')}
                                                                            showMandatoryCheckbox={allModelSides.length > 1}
                                                                            dragHandle={allModelSides.length > 1 ? dragHandle : undefined}
                                                                            onRemoveClick={() => handleSideRemoveClick(modelSide)}
                                                                            onDuplicateClick={() => handleSideDuplicateClick(modelSide)}
                                                                            onNameChangeClick={() => handleSideChangeName(modelSide)}
                                                                            onAreaChangeClick={() => handleSideChangeArea(modelSide)}
                                                                            onImageChangeClick={() => changeSideImageFromDevice(modelSide)}
                                                                            onGenerateImageWithAi={() => handleGenerateImageWithAi(modelSide)}
                                                                            onSetMandatorySide={(value) => handleSideChangeMandatory(modelSide, value)}
                                                                            onSetRotateSide={(value) => handleSideChangeRotate(modelSide, value)}
                                                                        />
                                                                    </ValidationBox>
                                                                </Section>
                                                            )}
                                                        </SortableSide>
                                                    );
                                                })}
                                            </SortableContext>
                                        </DndContext>

                                        {allModelSides.every((x) => x.areas.length > 0) && (
                                            <Section>
                                                <PrintAreaItem
                                                    showRemoveButton={false}
                                                    showMandatoryCheckbox={false}
                                                    modelColor={null}
                                                    modelSide={null}
                                                    onAddSideUpload={handleSideAddUpload}
                                                    onAddSideAi={handleSideAddAi}
                                                />
                                            </Section>
                                        )}
                                    </>
                                )}
                            </React.Fragment>
                        )}
                    </PageContainer>
                </MainColumn>
            </SetupLayout>

            {(!isResizable || state.product.colors.length > 1) && (state.product.imageUrl || !state.ecommerceSalesChannel.some((e) => e === 'NONE')) && (
                <PageFooterButtons $fixed={true} fullWidth>
                    {!state.product.id && query.seller?.marketplace === 'HP' && (
                        <BasicBetaButton onClick={handleOnSelectTemplate}>{T._('Set from template...', 'Admin')}</BasicBetaButton>
                    )}
                    {/* Footer for sales channels that support variants */}
                    {isCurrentSalesChannelSupportVariants && (
                        <>
                            <BasicButton
                                $size='large'
                                onClick={() => {
                                    if (
                                        state.product.name &&
                                        validateInputSecurity(state.product.name).isValid === true &&
                                        !state.sides.some((s) => !s.name || validateInputSecurity(s.name).isValid === false)
                                    )
                                        handleGoToVariationsButtonClick();
                                }}
                            >
                                {T._('Go to variants', 'Admin')}
                            </BasicButton>

                            {(state.product.id === null || (state.product.colors.length == 1 && isDefaultColor(state.product.colors[0]))) && (
                                <PrimaryButton
                                    $size='large'
                                    onClick={() => {
                                        if (
                                            state.product.name &&
                                            validateInputSecurity(state.product.name).isValid === true &&
                                            !state.sides.some((s) => !s.name || validateInputSecurity(s.name).isValid === false)
                                        )
                                            handleSkipVariationsClick();
                                        else {
                                            if (!state.product.name || validateInputSecurity(state.product.name).isValid === false) {
                                                showNotification({
                                                    message: T._('You need to set the product name', 'Admin'),
                                                    type: 'warning',
                                                    dismiss: {
                                                        duration: 4000
                                                    }
                                                });
                                            } else {
                                                showNotification({
                                                    message: T._('You need to set the name for all product sides', 'Admin'),
                                                    type: 'warning',
                                                    dismiss: {
                                                        duration: 4000
                                                    }
                                                });
                                            }
                                        }
                                    }}
                                >
                                    {T._(`I don't offer variants, go to review`, 'Admin')}
                                </PrimaryButton>
                            )}
                        </>
                    )}
                    {/* Footer for sales channels that don't support variants */}
                    {!isCurrentSalesChannelSupportVariants && (
                        <PrimaryButton
                            onClick={() => {
                                if (
                                    state.product.name &&
                                    validateInputSecurity(state.product.name).isValid === true &&
                                    !state.sides.some((s) => !s.name || validateInputSecurity(s.name).isValid === false)
                                )
                                    handleSkipVariationsClick();
                                else {
                                    if (!state.product.name || validateInputSecurity(state.product.name).isValid === false) {
                                        showNotification({
                                            message: T._('You need to set the product name', 'Admin'),
                                            type: 'warning',
                                            dismiss: {
                                                duration: 4000
                                            }
                                        });
                                    } else {
                                        showNotification({
                                            message: T._('You need to set the name for all product sides', 'Admin'),
                                            type: 'warning',
                                            dismiss: {
                                                duration: 4000
                                            }
                                        });
                                    }
                                }
                            }}
                        >
                            {T._('Continue to preview', 'Admin')}
                        </PrimaryButton>
                    )}
                </PageFooterButtons>
            )}
            {routeParams.subpage === 'print-area-editor' &&
                currentEditingModelSide &&
                createPortal(
                    <PrintAreaEditorDialog
                        modelColor={findModelColorForModelSide(state.product, currentEditingModelSide)!}
                        modelSide={currentEditingModelSide}
                        modelOptions={currentEditingModelOptions}
                        disabledModelSides={state.disabledModelSides}
                        onClose={() => {
                            window.history.back();
                        }}
                        onSave={(newModelSide, newModelOptions, isDirty, areasWereDeleted) => {
                            const closeEditor = () => navigate(-1);
                            const applyChanges = (modelSideToSave: ModelSide, modelOptionsToSave: ModelOptions | null) => {
                                if (areasWereDeleted) {
                                    dispatch(setAreasWereDeletedAction(true));
                                }

                                const currentColor = findModelColorForModelSide(state.product, currentEditingModelSide)!;

                                if (
                                    subscriptionResponse.subscription?.hasSidesPerVariant &&
                                    state.product.colors.length > 1 &&
                                    hasModelSideChanged(currentEditingModelSide, modelSideToSave)
                                ) {
                                    showMultipleVariantsQuestion(
                                        currentEditingModelSide,
                                        'PRINT_AREA',
                                        () => {
                                            dispatch(mergeModelSideAction(currentEditingModelSide, modelSideToSave));

                                            // Model options
                                            if (modelOptionsToSave) {
                                                dispatch(upsertModelOptionsAction(modelOptionsToSave));
                                            } else if (currentEditingModelOptions) {
                                                dispatch(removeModelOptionsAction(currentColor));
                                            }

                                            closeEditor();
                                        },
                                        () => {
                                            handleApplyAreaToAll(modelSideToSave, modelOptionsToSave);
                                            closeEditor();
                                        }
                                    );
                                } else {
                                    handleApplyAreaToAll(modelSideToSave, modelOptionsToSave);
                                    closeEditor();
                                }
                            };

                            if (isDirty) {
                                // Build detailed message showing what changed (dimensions and/or position)
                                const dimensionChanges: string[] = [];
                                const positionChanges: string[] = [];
                                for (const newArea of newModelSide.areas) {
                                    const oldArea = currentEditingModelSide.areas.find((a) => a.guid === newArea.guid);
                                    if (oldArea) {
                                        const oldWidth = round(oldArea.boundsWidth / currentEditingModelSide.ppcm, 3);
                                        const newWidth = round(newArea.boundsWidth / newModelSide.ppcm, 3);
                                        const oldHeight = round(oldArea.boundsHeight / currentEditingModelSide.ppcm, 3);
                                        const newHeight = round(newArea.boundsHeight / newModelSide.ppcm, 3);
                                        const oldX = round(oldArea.boundsX / currentEditingModelSide.ppcm, 3);
                                        const newX = round(newArea.boundsX / newModelSide.ppcm, 3);
                                        const oldY = round(oldArea.boundsY / currentEditingModelSide.ppcm, 3);
                                        const newY = round(newArea.boundsY / newModelSide.ppcm, 3);

                                        if (Math.abs(oldWidth - newWidth) > 0.001) {
                                            dimensionChanges.push(`${T._('Width', 'Admin')}: ${oldWidth} → ${newWidth} cm`);
                                        }
                                        if (Math.abs(oldHeight - newHeight) > 0.001) {
                                            dimensionChanges.push(`${T._('Height', 'Admin')}: ${oldHeight} → ${newHeight} cm`);
                                        }
                                        const positionThreshold = 0.01;
                                        const horizontal =
                                            Math.abs(oldX - newX) > positionThreshold
                                                ? newX > oldX
                                                    ? T._('to the right', 'Admin')
                                                    : T._('to the left', 'Admin')
                                                : '';
                                        const vertical =
                                            Math.abs(oldY - newY) > positionThreshold ? (newY > oldY ? T._('down', 'Admin') : T._('up', 'Admin')) : '';

                                        if (horizontal && vertical) {
                                            positionChanges.push(stringFormat(T._('Moved {0} and {1} compared to before', 'Admin'), horizontal, vertical));
                                        } else if (horizontal) {
                                            positionChanges.push(stringFormat(T._('Moved {0} compared to before', 'Admin'), horizontal));
                                        } else if (vertical) {
                                            positionChanges.push(stringFormat(T._('Moved {0} compared to before', 'Admin'), vertical));
                                        }
                                    }
                                }

                                const hasDimensions = dimensionChanges.length > 0;
                                const hasPosition = positionChanges.length > 0;

                                // If the user reverted back to the original bounds, skip the dialog entirely
                                if (!hasDimensions && !hasPosition) {
                                    applyChanges(newModelSide, newModelOptions);
                                    return;
                                }

                                let mainMessage: string;
                                if (hasDimensions && hasPosition) {
                                    mainMessage = T._(
                                        'There are unsaved changes to the print area position and dimensions. Do you want to keep them or discard them?',
                                        'Admin'
                                    );
                                } else if (hasPosition) {
                                    mainMessage = T._(
                                        'There are unsaved changes to the print area position. Do you want to keep them or discard them?',
                                        'Admin'
                                    );
                                } else {
                                    mainMessage = T._(
                                        'There are unsaved changes to the print area dimensions. Do you want to keep them or discard them?',
                                        'Admin'
                                    );
                                }

                                let detailMessage = '';
                                if (hasDimensions && hasPosition) {
                                    detailMessage =
                                        `${T._('Position', 'Admin')}:\n${positionChanges.join('\n')}\n\n` +
                                        `${T._('Dimensions', 'Admin')}:\n${dimensionChanges.join('\n')}`;
                                } else if (hasPosition) {
                                    detailMessage = positionChanges.join('\n');
                                } else if (hasDimensions) {
                                    detailMessage = dimensionChanges.join('\n');
                                }

                                showDialog(
                                    'unsaved-dimensions',
                                    <MessageDialog
                                        windowDecorator={LargeMessageDialogWindow}
                                        message={
                                            <>
                                                {mainMessage}
                                                {detailMessage && (
                                                    <div style={{ marginTop: '12px', fontWeight: 'bold', whiteSpace: 'pre-line' }}>{detailMessage}</div>
                                                )}
                                            </>
                                        }
                                        buttons={[
                                            {
                                                label: T._('Discard', 'Admin'),
                                                outline: true,
                                                onClick: () => {
                                                    closeDialog('unsaved-dimensions');
                                                    applyChanges(currentEditingModelSide, newModelOptions);
                                                }
                                            },
                                            {
                                                label: T._('Keep', 'Admin'),
                                                onClick: () => {
                                                    closeDialog('unsaved-dimensions');
                                                    applyChanges(newModelSide, newModelOptions);
                                                }
                                            }
                                        ]}
                                    />
                                );
                            } else {
                                applyChanges(newModelSide, newModelOptions);
                            }
                        }}
                    />,
                    dialogsPortal
                )}
        </React.Fragment>
    );
};

export default PrintAreas;
